import React from 'react';
import { Cloud, Settings, Users, Database, Briefcase, BarChart, Cpu, FileText, ShoppingCart, Globe, Terminal, Network, ShieldCheck, Zap, ChevronDown } from 'lucide-react';
import servicesBg from '../assets/images/servicesbg.png';
import { useNavigate } from 'react-router-dom';

const titleToId: { [key: string]: string } = {
    "CLOUD COMPUTING": "cloud-computing",
    "ENTERPRISE RESOURCE PLANNING": "erp",
    "CUSTOMER RELATIONSHIP MANAGEMENT": "crm",
    "B2B APPLICATIONS": "b2b-applications",
    "B2C APPLICATIONS": "b2c-applications",
    "E-COMMERCE SOLUTIONS": "e-commerce-solutions",
    "IT INFRASTRUCTURE MANAGEMENT": "it-infrastructure-management",
    "ENTERPRISE APPLICATION": "enterprise-application",
    "ROBOTIC PROCESS AUTOMATION": "robotic-process-automation",
    "BLOCKCHAIN TECHNOLOGY": "blockchain-technology"
};

const ServicesGrid = () => {
    const services = [
        {
            title: "CLOUD COMPUTING",
            icon: <Cloud className="w-10 h-10 text-[#2980b9]" />,
            description: "The most disruptive technology in the recent past that stole the limelight is undoubtedly cloud computing. Being rapidly adapted by rapid organizations the world over."
        },
        {
            title: "ENTERPRISE RESOURCE PLANNING",
            icon: <Settings className="w-10 h-10 text-[#34495e]" />,
            description: "While you focus on what is important, leave all your mundane activities to us. Among a basket of items, we help in designing and setting up smart ERP solutions that are entirely tailor-made."
        },
        {
            title: "CUSTOMER RELATIONSHIP MANAGEMENT",
            icon: <Users className="w-10 h-10 text-[#2980b9]" />,
            description: "Building better customer relations is a serious investment that every organization should cater to given the growing organization. How best you can make your customers happy."
        },
        {
            title: "B2B APPLICATIONS",
            icon: <Database className="w-10 h-10 text-[#3498db]" />,
            description: "Streamlining complex operations, our B2B applications help you and your clients to remain in tandem. It is important to keep in place industry wise, business specific process."
        },
        {
            title: "B2C APPLICATIONS",
            icon: <Globe className="w-10 h-10 text-[#2980b9]" />,
            description: "You have a service or a product in mind? Simply leave it to us to convert this idea into the most trending application that can earn you customers and revenue."
        },
        {
            title: "E-COMMERCE SOLUTIONS",
            icon: <ShoppingCart className="w-10 h-10 text-[#e67e22]" />,
            description: "Christening your footprints online, creating stunning storylines, increasing your organic growth and overall customer volume base is just the tip of our E-commerce solutions."
        },
        {
            title: "IT INFRASTRUCTURE MANAGEMENT",
            icon: <Network className="w-10 h-10 text-[#34495e]" />,
            description: "Building a robust infrastructure for the seamless functioning of your business is not only important but a necessity. From network management to data storage."
        },
        {
            title: "ENTERPRISE APPLICATION",
            icon: <Briefcase className="w-10 h-10 text-[#16a085]" />,
            description: "Custom-made enterprise solutions that enable your business to sail through everyday functions can now be left to us. We engineer Enterprise applications that take care of every."
        },
        {
            title: "ROBOTIC PROCESS AUTOMATION",
            icon: <Cpu className="w-10 h-10 text-[#2980b9]" />,
            description: "Exploring new ways to automate processes with limited human intervention, then our RPA software would be the one to eye at. RPA is powered with greater accuracy, speed."
        },
        {
            title: "BLOCKCHAIN TECHNOLOGY",
            icon: <ShieldCheck className="w-10 h-10 text-[#f39c12]" />,
            description: "The first thought of blockchain reminds us about Bitcoins, but this groundbreaking technology is now extensively implemented to various other complex business."
        }
    ];

    return (
        <section id="services" className="relative py-24 overflow-hidden">
            {/* Background Image */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${servicesBg})` }}
            />
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-black/50" />

            <div className="container mx-auto px-6 md:px-12 relative z-10">
                <h2 className="text-[#f39c12] text-3xl md:text-5xl font-bold mb-12 md:mb-20 uppercase text-center tracking-[0.1em] md:tracking-[0.2em]">
                    Services
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* First row of 4 */}
                    {services.slice(0, 4).map((service, index) => (
                        <ServiceCard key={index} service={service} />
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-4 lg:max-w-[50%] mx-auto">
                    {/* Middle row of 2 */}
                    {services.slice(4, 6).map((service, index) => (
                        <ServiceCard key={index} service={service} />
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Bottom row of 4 */}
                    {services.slice(6, 10).map((service, index) => (
                        <ServiceCard key={index} service={service} />
                    ))}
                </div>
            </div>
        </section>
    );
};

const ServiceCard = ({ service }: { service: any }) => {
    const navigate = useNavigate();
    const handleShowMore = () => {
        const id = titleToId[service.title];
        navigate(`/services#${id}`);
    };

    return (
        <div className="bg-black/40 backdrop-blur-sm border-[1.5px] border-[#f39c12] p-5 flex flex-col h-full group transition-all duration-300">
            <div className="mb-4">{service.icon}</div>
            <h3 className="text-white font-bold mb-4 text-[13px] tracking-tight leading-tight">{service.title}</h3>
            <p className="text-white/90 text-[12px] mb-8 leading-relaxed flex-grow">{service.description}</p>
            <div className="flex justify-end">
                <button
                    onClick={handleShowMore}
                    className="flex items-center gap-1 text-white border border-white/60 bg-white/5 py-1 px-3 text-[11px] hover:bg-white/10 transition-colors"
                >
                    Show More <ChevronDown className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
};

export default ServicesGrid;

